'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useContext, useEffect, useState } from 'react'
import { Formik } from 'formik'

// walrus
import Box from '@components/kit/box'
import Card from '@components/kit/card'
import Text from '@components/kit/text'
import { Row } from '@components/kit/grid'

// lib
import FaxQuill from './fax-quill'
import Form, {
  CheckBoxField,
  PhoneField,
  PhoneFieldProps,
  TextField,
  TextFieldProps
} from '@components/kit/form'

import { useFaxStore } from '@store/Fax'
import Loading from '@components/kit/loading'
import Button from '@components/kit/button'
import { ChatContext } from '../chat.web'
import Modal from '@components/kit/modal'
import { HorizontalRule } from '@components/table'

const RESOLUTION_TIMEOUT = 500

/* -----------------------------------------------------------------------------
 * TextFieldFormated
 * -------------------------------------------------------------------------- */

interface InputFieldFormatedProps {
  value: string
  onChangeText: (text: string) => void
  FieldComponent: React.ComponentType<any>
}

const InputFieldFormated = ({
  value,
  onChangeText,
  FieldComponent,
  ...otherProps
}: InputFieldFormatedProps) => {
  const [isSelected, setIsSelected] = useState(false)

  return (
    <FieldComponent
      value={value}
      onChangeText={onChangeText}
      onFocus={() => setIsSelected(true)}
      onBlur={() => setIsSelected(false)}
      required
      placeholder='Search for a medication'
      mb='04'
      borderColor={isSelected ? 'walrusBlue500' : 'transparent'}
      borderWidth={2}
      textColor={
        isSelected
          ? 'walrusBlue500'
          : value.length > 0
          ? 'walrusBlue200'
          : 'shade06'
      }
      font='display.14.bold'
      placeholderFont='display.12.bold'
      labelFont='display.12.bold'
      placeholderTextColor='shade05'
      labelTextColor={
        value.length > 0 || isSelected ? '#404CABCC' : 'walrusBlue700'
      }
      backgroundColor={isSelected ? 'white' : 'walrusBlue050'}
      {...otherProps}
    />
  )
}

interface TextFieldFormatedProps extends TextFieldProps {
  value: string
  onChangeText: (text: string) => void
}

const TextFieldFormated = ({
  value,
  onChangeText,
  ...otherProps
}: TextFieldFormatedProps) => {
  return (
    <InputFieldFormated
      FieldComponent={TextField}
      value={value}
      onChangeText={onChangeText}
      {...otherProps}
    />
  )
}

interface PhoneFieldFormatedProps extends PhoneFieldProps {
  value: string
  onChangeText: (text: string) => void
}

const PhoneFieldFormated = ({
  value,
  onChangeText,
  ...otherProps
}: PhoneFieldFormatedProps) => {
  return (
    <InputFieldFormated
      FieldComponent={PhoneField}
      value={value}
      onChangeText={onChangeText}
      {...otherProps}
    />
  )
}

/* -----------------------------------------------------------------------------
 * FaxForm
 * -------------------------------------------------------------------------- */

function FaxForm() {
  const { userId } = useContext(ChatContext)

  const [faxContent, setFaxContent] = useState('')
  const [faxTitle, setFaxTitle] = useState('')
  const [faxReceiverNumber, setFaxReceiverNumber] = useState('')
  const [faxReceiverName, setFaxReceiverName] = useState('')
  const [isActionRequired, setIsActionRequired] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isTakingLong, setIsTakingLong] = useState(false)
  const [checkIfIsSubmitting, setCheckIfIsSubmitting] = useState(false)

  const { sendFax } = useFaxStore.getState()

  const handleFormClear = () => {
    if (isSuccess) {
      setFaxContent('')
      setFaxTitle('')
      setFaxReceiverNumber('')
      setFaxReceiverName('')
      setIsActionRequired(false)
    }
    setIsSuccess(false)
    setIsError(false)
    setIsTakingLong(false)
  }

  const handleSubmit = () => {
    setIsSubmitting(true)

    sendFax({
      user_id: userId,
      fax_number: faxReceiverNumber,
      receiver: faxReceiverName,
      title: faxTitle,
      message: faxContent,
      action_required: isActionRequired
    })
      .then(success => {
        setTimeout(() => {
          if (success) setIsSuccess(true)
          else setIsError(true)
        }, RESOLUTION_TIMEOUT)
      })
      .catch(() => {
        setTimeout(() => {
          setIsError(true)
        }, RESOLUTION_TIMEOUT)
      })
      .finally(() => {
        setTimeout(() => {
          setIsSubmitting(false)
        }, RESOLUTION_TIMEOUT)
      })

    setTimeout(() => {
      setCheckIfIsSubmitting(true)
    }, 10000)
  }

  useEffect(() => {
    if (checkIfIsSubmitting && isSubmitting) {
      setIsTakingLong(true)
    }
  }, [checkIfIsSubmitting, isSubmitting])

  return (
    <React.Fragment>
      <Box flexDirection='column' alignItems='flex-start' mt='04'>
        <Row width='100%'>
          <Text ml='12' font='display.16.bold'>
            Send Fax
          </Text>
        </Row>
        <Box mt='08' flexDirection='row' gap='04'>
          {/* for future implementations: nav tools */}
        </Box>
        <Card width={'100%'} mt='08' px='00' py='00'>
          <Row flex='1' width='100%' padding='16'>
            <Formik
              initialValues={{ medication: '' }}
              onSubmit={() => {}}
              validateOnChange={true}
            >
              <Form flex='1' gap='16'>
                <TextFieldFormated
                  name='receiver-name'
                  label='Receiver Name'
                  value={faxReceiverName}
                  required
                  onChangeText={setFaxReceiverName}
                  showErrorMessage
                />
                <PhoneFieldFormated
                  name='receiver-number'
                  label='Receiver Number'
                  value={faxReceiverNumber}
                  required
                  onChangeText={setFaxReceiverNumber}
                />
                <TextFieldFormated
                  name='title'
                  label='Title'
                  value={faxTitle}
                  required
                  onChangeText={setFaxTitle}
                />

                <FaxQuill
                  faxContent={faxContent}
                  setFaxContent={setFaxContent}
                />
                <Box
                  width='100%'
                  flexDirection='column'
                  alignItems='flex-end'
                  gap='32'
                >
                  <CheckBoxField
                    name='action-required'
                    label='Action Required'
                    value={isActionRequired}
                    onValueChange={setIsActionRequired}
                    font='display.14.bold'
                    textColor='walrusBlue200'
                  />
                  <Button
                    variant='secondary'
                    width='fit-content'
                    onPress={handleSubmit}
                  >
                    Send fax
                  </Button>
                </Box>
              </Form>
            </Formik>
          </Row>
        </Card>
      </Box>
      {isSubmitting && (
        <Box
          position='absolute'
          width='100%'
          height='100%'
          top='0'
          left='0'
          right='0'
          flex='1'
          justifyContent='center'
          alignItems='center'
          style={{
            backgroundColor: '#f4f7facc'
          }}
        >
          <Loading size='large' />
        </Box>
      )}
      {(isSuccess || isError || isTakingLong) && (
        <React.Fragment>
          <Box
            position='absolute'
            width='100%'
            height='100%'
            top='0'
            left='0'
            right='0'
            flex='1'
            justifyContent='center'
            alignItems='center'
            style={{
              backgroundColor: '#f4f7facc'
            }}
          />
          <Modal
            open={isSuccess || isError || isTakingLong}
            onDismiss={handleFormClear}
            width='400px'
            backgroundColor={
              isSuccess ? 'mintGreen' : isError ? 'walrusRed050' : 'lightOrange'
            }
          >
            <Text
              variant='h3'
              textAlign='center'
              margin='32'
              textColor={
                isSuccess ? 'paleGreen' : isError ? 'paleRed' : 'orange'
              }
            >
              {isSuccess
                ? 'Fax was sent successfully!'
                : isError
                ? 'An error has occurred.\nThe fax was not sent.'
                : isTakingLong &&
                  'Sending the fax is taking longer than expected. What do you want to do?'}
            </Text>
            <HorizontalRule />
            <Box
              flexDirection='row'
              justifyContent='center'
              gap='16'
              margin='32'
            >
              <Button variant='secondary' onPress={handleFormClear}>
                Close
              </Button>
              {isTakingLong && !isSuccess && !isError && (
                <Button
                  variant='redGradient'
                  onPress={() => {
                    setIsSubmitting(false)
                    handleFormClear()
                  }}
                >
                  Stop
                </Button>
              )}
            </Box>
          </Modal>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default FaxForm
